import { TYPED_RESPONSE_LIMIT } from './hints';
import type { AnswerKey, AnswerResponse } from './question';
import { normalizeText, trimReadingAnswer } from './text-matching';

/** Real AI checks a child may cost per hour. Exact matches, rules and stored decisions are free. */
export const ANSWER_CHECKS_PER_HOUR = 60;

/** Only reading comprehension judges meaning. Spelling, taal and rekenen need the exact form. */
export const MEANING_CHECK_SUBJECT = 'begrijpend_lezen';

/** Who decided whether an answer was right. Stored on every answer. */
export const ANSWER_GRADERS = ['exact', 'rule', 'cache', 'ai', 'staff'] as const;
export type AnswerGrader = (typeof ANSWER_GRADERS)[number];

/** Where a stored meaning decision came from. Staff decisions are never overwritten by the AI. */
export const ANSWER_DECISION_SOURCES = ['ai', 'staff'] as const;
export type AnswerDecisionSource = (typeof ANSWER_DECISION_SOURCES)[number];

export function usesReadingRules(subjectKey: string): boolean {
  return subjectKey === MEANING_CHECK_SUBJECT;
}

/**
 * The key a meaning decision is stored under: the typed answer with spacing, case,
 * a leading article and trailing punctuation evened out. Empty when nothing useful was typed,
 * and empty when the answer is longer than a typed answer may be, so a long answer is never
 * judged on a clipped part.
 */
export function answerCheckKey(value: string): string {
  const normalized = normalizeText(value, { caseSensitive: false });
  if (normalized.length > TYPED_RESPONSE_LIMIT) {
    return '';
  }
  return normalizeText(trimReadingAnswer(normalized), { caseSensitive: false });
}

/** Whether a typed answer that the rules marked wrong may still be judged on its meaning. */
export function needsMeaningCheck(
  subjectKey: string,
  key: AnswerKey,
  response: AnswerResponse,
): boolean {
  return (
    usesReadingRules(subjectKey) &&
    key.type === 'text' &&
    response.type === 'text' &&
    answerCheckKey(response.value) !== ''
  );
}
